/**
 * questionari/traduzioni — un questionario nella lingua di chi lo compila.
 * ============================================================================
 *
 * Segue la regola dei testi multilingua di tutto il gestionale
 * (`functions/src/shared/i18n/testoMultilingua.ts`):
 *  1. i testi di primo livello sono nella `lingua_sorgente`, le altre lingue in `traduzioni[lingua]`,
 *     il cui valore è SEMPRE un oggetto (qui indicizzato per codice di domanda, opzione, campo);
 *  2. la lingua sorgente è un DATO, e non cambia;
 *  3. la mappa si guarda SEMPRE per prima, anche nella lingua sorgente: un testo scritto lì si vede;
 *  4. una stringa vuota vale come assente: un campo svuotato non batte il testo che c'era.
 * Il ripiego si conta CAMPO PER CAMPO (`mancanti`): la console mostra che cosa manca in quella
 * lingua; la pagina pubblica mostra comunque un testo, quello sorgente.
 *
 * Cosa NON si traduce, mai: gli id (domande, opzioni, campi) e i valori salvati nelle risposte.
 * Le statistiche contano codici, quindi due persone che compilano in due lingue finiscono nello
 * stesso conteggio.
 *
 * File puro, target ES2017.
 */

import type {
  CampoContatto,
  Domanda,
  EmailConferma,
  Questionario,
  SchermataApertura,
  SchermataConferma,
  SchermataContatti,
  TraduzioneDomanda,
  TraduzioneQuestionario,
} from './tipi';

/** Una lingua come codice di due lettere minuscole; altrimenti `null`. */
export function codiceLingua(v: unknown): string | null {
  const l = typeof v === 'string' ? v.trim().toLowerCase().slice(0, 2) : '';
  return /^[a-z]{2}$/.test(l) ? l : null;
}

export function linguaSorgente(q: { lingua_sorgente?: string | null }): string {
  return codiceLingua(q.lingua_sorgente) ?? 'it';
}

function pieno(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim() ? v : undefined;
}

export interface QuestionarioTradotto {
  questionario: Questionario;
  /** La lingua dei testi: quella richiesta, o la sorgente se la richiesta non è valida. */
  lingua: string;
  /** I testi che in questa lingua mancano e si leggono nella sorgente (percorsi leggibili). */
  mancanti: string[];
}

/**
 * Il questionario coi testi nella lingua richiesta. Non cambia mai la struttura (domande,
 * opzioni, obblighi): solo le parole.
 */
export function questionarioInLingua(q: Questionario, linguaRichiesta: unknown): QuestionarioTradotto {
  const sorgente = linguaSorgente(q);
  const lingua = codiceLingua(linguaRichiesta) ?? sorgente;
  const voce: TraduzioneQuestionario = (q.traduzioni && q.traduzioni[lingua]) || {};
  const mancanti: string[] = [];
  const contaMancanti = lingua !== sorgente;

  /** Prima la traduzione, poi la sorgente (contata come mancante), poi niente. */
  function scegli(percorso: string, tradotto: unknown, originale: string | undefined): string | undefined {
    const t = pieno(tradotto);
    if (t !== undefined) return t;
    const o = pieno(originale);
    if (o !== undefined && contaMancanti) mancanti.push(percorso);
    return originale;
  }
  /**
   * Un INDIRIZZO (informativa, collegamento): si può cambiare per lingua (/it/privacy → /en/privacy),
   * ma è un riferimento, non un testo che si legge, quindi non conta mai come traduzione mancante.
   */
  function scegliRiferimento(tradotto: unknown, originale: string | undefined): string | undefined {
    return pieno(tradotto) ?? originale;
  }
  /** Come `scegli`, per un campo obbligatorio della forma (mai undefined). */
  function scegliTesto(percorso: string, tradotto: unknown, originale: string): string {
    return scegli(percorso, tradotto, originale) ?? originale;
  }

  const domande = q.domande.map((d) => tradurreDomanda(d, (voce.domande && voce.domande[d.id]) || {}, scegli, scegliTesto));

  let apertura: SchermataApertura | null | undefined = q.apertura;
  if (q.apertura) {
    const t = voce.apertura || {};
    apertura = { ...q.apertura, titolo: scegliTesto('apertura.titolo', t.titolo, q.apertura.titolo) };
    assegna(apertura, 'testo', scegli('apertura.testo', t.testo, q.apertura.testo));
    assegna(apertura, 'etichetta', scegli('apertura.etichetta', t.etichetta, q.apertura.etichetta));
    assegna(apertura, 'bottone', scegli('apertura.bottone', t.bottone, q.apertura.bottone));
  }

  let contatti: SchermataContatti | null = q.contatti;
  if (q.contatti) {
    const t = voce.contatti || {};
    const tc = t.campi || {};
    contatti = {
      ...q.contatti,
      domanda: scegliTesto('contatti.domanda', t.domanda, q.contatti.domanda),
      campi: q.contatti.campi.map((c) => {
        const tcampo = tc[c.id] || {};
        const campo: CampoContatto = { ...c, etichetta: scegliTesto(`contatti.campi.${c.id}.etichetta`, tcampo.etichetta, c.etichetta) };
        assegna(campo, 'segnaposto', scegli(`contatti.campi.${c.id}.segnaposto`, tcampo.segnaposto, c.segnaposto));
        assegna(campo, 'nota', scegli(`contatti.campi.${c.id}.nota`, tcampo.nota, c.nota));
        return campo;
      }),
    };
    assegna(contatti, 'indicatore', scegli('contatti.indicatore', t.indicatore, q.contatti.indicatore));
    assegna(contatti, 'aiuto', scegli('contatti.aiuto', t.aiuto, q.contatti.aiuto));
    assegna(contatti, 'bottone', scegli('contatti.bottone', t.bottone, q.contatti.bottone));
    if (q.contatti.consenso) {
      const tk = t.consenso || {};
      const consenso = { ...q.contatti.consenso, testo: scegliTesto('contatti.consenso.testo', tk.testo, q.contatti.consenso.testo) };
      assegna(consenso, 'etichetta_informativa', scegli('contatti.consenso.etichetta_informativa', tk.etichetta_informativa, q.contatti.consenso.etichetta_informativa));
      assegna(consenso, 'url_informativa', scegliRiferimento(tk.url_informativa, q.contatti.consenso.url_informativa));
      contatti.consenso = consenso;
    }
  }

  let conferma: SchermataConferma | null | undefined = q.conferma;
  if (q.conferma) {
    const t = voce.conferma || {};
    conferma = { ...q.conferma, titolo: scegliTesto('conferma.titolo', t.titolo, q.conferma.titolo) };
    assegna(conferma, 'in_grande', scegli('conferma.in_grande', t.in_grande, q.conferma.in_grande));
    assegna(conferma, 'testo', scegli('conferma.testo', t.testo, q.conferma.testo));
    assegna(conferma, 'testo_se_consenso', scegli('conferma.testo_se_consenso', t.testo_se_consenso, q.conferma.testo_se_consenso));
    if (q.conferma.collegamento) {
      const tl = t.collegamento || {};
      const c = q.conferma.collegamento;
      conferma.collegamento = {
        prima: scegliTesto('conferma.collegamento.prima', tl.prima, c.prima),
        etichetta: scegliTesto('conferma.collegamento.etichetta', tl.etichetta, c.etichetta),
        url: scegliRiferimento(tl.url, c.url) ?? c.url,
      };
    }
  }

  let email: EmailConferma | null | undefined = q.email_conferma;
  if (q.email_conferma) {
    const t = voce.email_conferma || {};
    const e = q.email_conferma;
    email = {
      ...e,
      oggetto: scegliTesto('email_conferma.oggetto', t.oggetto, e.oggetto),
      titolo: scegliTesto('email_conferma.titolo', t.titolo, e.titolo),
      testo: scegliTesto('email_conferma.testo', t.testo, e.testo),
      perche: scegliTesto('email_conferma.perche', t.perche, e.perche),
    };
    const facoltativi: Array<Exclude<keyof EmailConferma, 'attiva' | 'oggetto' | 'titolo' | 'testo' | 'perche'>> = [
      'anteprima',
      'in_grande',
      'titolo_riepilogo',
      'testo_consenso',
      'bottone_consenso',
      'etichetta_uscita',
    ];
    facoltativi.forEach((k) => assegna(email!, k, scegli(`email_conferma.${k}`, t[k], e[k])));
  }

  const tradotto: Questionario = {
    ...q,
    titolo: scegliTesto('titolo', voce.titolo, q.titolo),
    domande,
    apertura,
    contatti,
    conferma,
    email_conferma: email,
  };
  const descrizione = scegli('descrizione', voce.descrizione, q.descrizione ?? undefined);
  tradotto.descrizione = descrizione ?? q.descrizione ?? null;
  return { questionario: tradotto, lingua, mancanti };
}

type Scegli = (percorso: string, tradotto: unknown, originale: string | undefined) => string | undefined;
type ScegliTesto = (percorso: string, tradotto: unknown, originale: string) => string;

function tradurreDomanda(d: Domanda, t: TraduzioneDomanda, scegli: Scegli, scegliTesto: ScegliTesto): Domanda {
  const p = `domande.${d.id}`;
  const out: Domanda = { ...d, testo: scegliTesto(`${p}.testo`, t.testo, d.testo) };
  assegna(out, 'nome_breve', scegli(`${p}.nome_breve`, t.nome_breve, d.nome_breve));
  assegna(out, 'aiuto', scegli(`${p}.aiuto`, t.aiuto, d.aiuto));
  assegna(out, 'segnaposto', scegli(`${p}.segnaposto`, t.segnaposto, d.segnaposto));
  if (d.opzioni) {
    const to = t.opzioni || {};
    out.opzioni = d.opzioni.map((o) => ({ ...o, etichetta: scegliTesto(`${p}.opzioni.${o.id}`, to[o.id], o.etichetta) }));
  }
  if (d.campo_dettaglio) {
    const tc = t.campo_dettaglio || {};
    const cd = { ...d.campo_dettaglio, etichetta: scegliTesto(`${p}.campo_dettaglio.etichetta`, tc.etichetta, d.campo_dettaglio.etichetta) };
    assegna(cd, 'segnaposto', scegli(`${p}.campo_dettaglio.segnaposto`, tc.segnaposto, d.campo_dettaglio.segnaposto));
    out.campo_dettaglio = cd;
  }
  if (d.scorciatoie) {
    const ts = t.scorciatoie || {};
    out.scorciatoie = d.scorciatoie.map((s) => {
      const copia = { ...s };
      assegna(copia, 'etichetta', scegli(`${p}.scorciatoie.${s.valore}`, ts[String(s.valore)], s.etichetta));
      return copia;
    });
  }
  return out;
}

/** Scrive il campo solo se ha un valore: niente chiavi `undefined` nei documenti. */
function assegna<T extends object, K extends keyof T>(obj: T, chiave: K, valore: T[K] | undefined): void {
  if (valore === undefined) delete obj[chiave];
  else obj[chiave] = valore;
}

/** Le lingue in cui il questionario ha almeno una traduzione (più la sorgente, prima). */
export function lingueDelQuestionario(q: Pick<Questionario, 'lingua_sorgente' | 'traduzioni'>): string[] {
  const sorgente = linguaSorgente(q);
  const altre = Object.keys(q.traduzioni || {})
    .map((l) => codiceLingua(l))
    .filter((l): l is string => !!l && l !== sorgente);
  return [sorgente].concat(altre.filter((l, i) => altre.indexOf(l) === i));
}
